import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import withRouter from "Components/Common/withRouter"
import ClientEngagementForm from "Components/organisms/forms/client-engagement-form"
import ClientEngagementFields from "Components/organisms/forms/client-engagement-form/types"
import FormPageTemplate from "Components/templates/form-page-template"
import { ClientEngagementValidation } from "data/client-engagement"
import { FormikHelpers, useFormik } from "formik"
import useManual from "hooks/useManual"
import Client from "models/client"
import Opportunity from "models/opportunity"
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { addClientEngagement, ClientEngagementSliceState, setClientEngagement, setClientEngagementSearchFields } from "slices/business-development/client-engagement/reducer"
import { getClientEngagementSearchFields, submitEditClientEngagementForm, getClientEngagement } from "slices/thunk"
import Modal from "Components/molecule/modal";
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import { NavigateFunction, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { ClientEngagement } from "models/client-engagement"


const selectClientEngagement = createSelector(
    (state: any) => state.ClientEngagement,
    ({
        opportunities,
        clients,
        clientEngagements
    }: ClientEngagementSliceState) => ({
        opportunities,
        clients,
        clientEngagements
    })
)

const EditClientEngagementPage = (props: any) => {
    document.title = "Edit Client Engagement | IbexIQ";
    const { handleOpenManual } = useManual("business-development/client-engagement");
    const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [focusedClientEngagement, setFocusedClientEngagement] = useState<ClientEngagement | null>(null);
    const dispatch: any = useDispatch();

    const [searchParams] = useSearchParams();
    const {
        opportunities,
        clients,
        clientEngagements
    } = useSelector(selectClientEngagement)

    const initializePage = async (clientEngagementId: string, navigate: NavigateFunction) => {

        if (
            opportunities.length === 0 ||
            clients.length === 0
        ) {
            const res = await getClientEngagementSearchFields()
            if (!res) {
                return;
            }
            dispatch(setClientEngagementSearchFields(res))
        }

        const focusedClientEngagement = await getClientEngagement(clientEngagementId)
        if (!focusedClientEngagement) {
            toast.error("Something went wrong. Could not find Client Engagement")
            navigate(-1);
            return;
        }

        setFocusedClientEngagement(focusedClientEngagement)
        setIsLoading(false)

    }

    useEffect(() => {
        const clientEngagementId = searchParams.get("clientEngagementId");


        if (!clientEngagementId) {
            toast.error("Something went wrong. Client Engagement Id not found");
            props.router.navigate(-1)
        } else {
            initializePage(clientEngagementId, props.router.navigate);
        }
    }, [searchParams, props.router.navigate])


    const handleSubmitClientEngagementForm = (_vals: ClientEngagementFields, helpers: FormikHelpers<ClientEngagementFields>) => {
        helpers.setSubmitting(false)
        setShowAreYouSureModal(true)
    }

    const initialValues: ClientEngagementFields = useMemo(() => ({
        opportunityId: focusedClientEngagement?.opportunityId ?? '',
        clientId: focusedClientEngagement?.clientId ?? '',
        notes: focusedClientEngagement?.notes ?? '',
        dateOfNextCheckin: focusedClientEngagement?.dateOfNextCheckin?.toString().split("T")[0] ?? undefined
    }), [focusedClientEngagement])

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        isValid,
        setSubmitting,
        isSubmitting,
        handleReset
    } = useFormik({
        onSubmit: handleSubmitClientEngagementForm,
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: ClientEngagementValidation
    })

    const handleBack = () => {
        props.router.navigate(-1)
        setShowAreYouSureModal(false)
    }

    const handleModalClose = () => setShowAreYouSureModal(false);

    const hanldeConfirmSubmitClientEngagementForm = useCallback(async (clientEngagementId: string, values: ClientEngagementFields, setSubmitting: (val: boolean) => any, reset: (e: any) => void) => {
        setSubmitting(true);
        const res = await submitEditClientEngagementForm(clientEngagementId, values, () => reset({}))
        if (!res) {
            handleModalClose()
        } else {
            handleBack()
            const index = clientEngagements.findIndex(c => c.id === clientEngagementId);
            dispatch(setClientEngagement({
                clientEngagement: res,
                index: index
            }))
        }
        setSubmitting(false)
    }, [handleBack, handleModalClose])


    return <FormPageTemplate
        title={"Business Development"}
        breadcrumbItem={"Edit Client Engagement"}
        actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
        form={<ClientEngagementForm
            clients={[...clients.map((bA: Client) => ({ value: bA.id, label: bA.name }))]}
            opportunities={[...opportunities.map((o: Opportunity) => ({ value: o.id, label: o.description }))]}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            isValid={isValid}
            isSubmitting={isSubmitting}
        />}
        isLoading={isLoading}
        modal={<Modal
            isOpen={showAreYouSureModal}
            onClose={handleModalClose}
            title={"Confirm"}
            subtitle={"Are you sure want to edit client engagement?"}
        >
            <AreYouSureButtons
                disable={isSubmitting}
                acceptButtonStyle={{ width: '150px' }}
                declineButtonStyle={{ width: '150px' }}
                handleAccept={() => hanldeConfirmSubmitClientEngagementForm(searchParams.get("clientEngagementId")!, values, setSubmitting, handleReset)}
                handleDecline={handleModalClose}
                acceptText={isSubmitting ? "Submitting..." : "Submit"}
                declineText={"Cancel"}
            />
        </Modal>}
    />
}

export default withRouter(EditClientEngagementPage);